/**
 * AnalyticsSettingsPage
 *
 * Project-level analytics integrations management page.
 * Allows adding/managing analytics platforms for all web funnels.
 *
 * Note: GDPR consent is now configured per-funnel in WebPlacementDialog.
 *
 * @module analytics-settings/pages
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Paper,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { WithGuideHelp } from '@modules/ui-components';
import { GuideService } from '@modules/firebase-core/services/GuideService';
import { GUIDE_ANCHORS } from '@modules/guides/anchors/guideAnchorRegistry';
import { useProject } from '@modules/navigation/contexts';
import { useAnalyticsSettings } from '../hooks/useAnalyticsSettings';
import { IntegrationsList } from '../components/IntegrationsList';
import { AddIntegrationDialog } from '../components/AddIntegrationDialog';
import { CustomDomainSection } from '@modules/custom-domains';
import type { AnalyticsPlatform, PlatformConfig } from '../types/analytics.types';
import { ANALYTICS_SETTINGS_TEST_IDS } from '@modules/analytics-settings/testIds';
import { useGuideHighlight } from '@modules/guides/hooks/useGuideHighlight';

/**
 * AnalyticsSettingsPage - Main page for analytics configuration
 */
export const AnalyticsSettingsPage: React.FC = () => {
  const { projectId } = useProject();
  useGuideHighlight();
  const guideService = useMemo(() => new GuideService(), []);

  const {
    settings,
    loading,
    error,
    saving,
    addIntegration,
    updateIntegration,
    deleteIntegration,
    toggleIntegration,
  } = useAnalyticsSettings(projectId ?? undefined);

  // Add integration dialog state
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  // Handle add integration
  const handleAddIntegration = useCallback(
    async (platform: AnalyticsPlatform, name: string, config: PlatformConfig) => {
      await addIntegration(platform, name, config);
      setAddDialogOpen(false);
    },
    [addIntegration]
  );

  // Show loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load analytics settings: {error.message}
        </Alert>
      </Box>
    );
  }

  // No project selected
  if (!projectId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Please select a project to configure analytics.</Alert>
      </Box>
    );
  }

  // D-16 / D-25: with zero integrations only the centred empty state offers "add" —
  // the header Add button and the "automatically injected" line appear once one exists,
  // so `analytics-add-integration-btn` is always exactly ONE node.
  const hasIntegrations = settings.integrations.length > 0;

  return (
    <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4,
        }}
      >
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Analytics Settings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure analytics integrations for all web funnels in this project.
          </Typography>
        </Box>

        {hasIntegrations && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setAddDialogOpen(true)}
            disabled={saving}
            data-testid={ANALYTICS_SETTINGS_TEST_IDS.addBtn}
          >
            Add Integration
          </Button>
        )}
      </Box>

      {/* Custom Domain — funnel slot only; the article domain lives in Articles → Site & domain (S-4).
          One help icon on the title carries the merged domain guide (D-23). */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          <WithGuideHelp
            guideKey={GUIDE_ANCHORS.customDomainSection.guideKey}
            guideService={guideService}
            data-testid={GUIDE_ANCHORS.customDomainSection.testId}
            position="inline-end"
          >
            Custom Domain
          </WithGuideHelp>
        </Typography>
        <CustomDomainSection projectId={projectId} />
      </Paper>

      {/* Integrations List */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Integrations
        </Typography>
        {hasIntegrations && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            All enabled integrations are automatically injected into every published web funnel.
          </Typography>
        )}

        <IntegrationsList
          integrations={settings.integrations}
          onUpdate={updateIntegration}
          onDelete={deleteIntegration}
          onToggle={toggleIntegration}
          disabled={saving}
        />

        {!hasIntegrations && (
          <Box
            sx={{
              textAlign: 'center',
              py: 6,
              color: 'text.secondary',
            }}
          >
            <Typography variant="body1" gutterBottom>
              No analytics integrations configured yet.
            </Typography>
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={() => setAddDialogOpen(true)}
              disabled={saving}
              sx={{ mt: 2 }}
              data-testid={ANALYTICS_SETTINGS_TEST_IDS.addBtn}
            >
              Add Your First Integration
            </Button>
          </Box>
        )}
      </Paper>

      {/* Add Integration Dialog */}
      <AddIntegrationDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onAdd={handleAddIntegration}
        existingIntegrations={settings.integrations}
      />
    </Box>
  );
};

export default AnalyticsSettingsPage;
